/**
 * usePayPalScript Hook
 *
 * Custom hook for loading the PayPal JavaScript SDK dynamically.
 * Handles script loading, error states, and ensures the script is only loaded once.
 *
 * The PayPal Client ID is loaded from the server configuration (admin settings).
 *
 * @module hooks/usePayPalScript
 */

import { useState, useEffect, useRef } from 'react';

/**
 * PayPal SDK configuration constants
 */
export const PAYPAL_SDK_CONFIG = {
  currency: 'EUR',
  intent: 'capture',
  locale: 'fr_FR',
} as const;

/**
 * PayPal button style configuration
 */
export const PAYPAL_BUTTON_STYLE = {
  layout: 'vertical' as const,
  color: 'white' as const,
  shape: 'pill' as const,
  label: 'pay' as const,
};

/**
 * PayPal experience context configuration
 */
export const PAYPAL_EXPERIENCE = {
  brand_name: 'Budgira',
  user_action: 'PAY_NOW',
  locale: 'fr-FR',
} as const;

/**
 * Return type for usePayPalScript hook
 */
export interface UsePayPalScriptReturn {
  /** Whether the PayPal SDK has finished loading successfully */
  isLoaded: boolean;
  /** Whether the PayPal SDK is currently loading */
  isLoading: boolean;
  /** Error that occurred during script loading, if any */
  error: Error | null;
}

// Script loading states tracked globally to ensure single load
let scriptLoadingPromise: Promise<void> | null = null;
let scriptLoaded = false;
let scriptError: Error | null = null;
let loadedClientId: string | null = null;

/**
 * Generates the PayPal SDK URL with the provided client ID and configuration
 */
function getPayPalScriptUrl(clientId: string): string {
  const params = new URLSearchParams({
    'client-id': clientId,
    components: 'buttons',
    currency: PAYPAL_SDK_CONFIG.currency,
    'integration-date': '2024-01-01',
    locale: PAYPAL_SDK_CONFIG.locale,
    vault: 'true', // Required for subscriptions
  });

  return `https://www.paypal.com/sdk/js?${params.toString()}`;
}

/**
 * Removes the existing PayPal script and resets state
 */
function removePayPalScript(): void {
  const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
  if (existingScript) {
    existingScript.remove();
  }
  // Clear PayPal from window
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).paypal) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (window as any).paypal;
  }
  // Reset state
  scriptLoadingPromise = null;
  scriptLoaded = false;
  scriptError = null;
  loadedClientId = null;
}

/**
 * Loads the PayPal SDK script dynamically
 */
function loadPayPalScript(clientId: string): Promise<void> {
  // If clientId changed, remove old script and reload
  if (loadedClientId && loadedClientId !== clientId) {
    console.log('[PayPal] Client ID changed, reloading script');
    removePayPalScript();
  }

  // If already loaded with same clientId, resolve immediately
  if (scriptLoaded && loadedClientId === clientId) {
    return Promise.resolve();
  }

  // If already loading, return the existing promise
  if (scriptLoadingPromise) {
    return scriptLoadingPromise;
  }

  // If there was a previous error, reset and try again
  if (scriptError) {
    scriptError = null;
  }

  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    // Check if script already exists in DOM with correct clientId
    const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
    if (existingScript) {
      const scriptSrc = existingScript.getAttribute('src') || '';
      const scriptClientId = new URLSearchParams(scriptSrc.split('?')[1]).get('client-id');

      if (scriptClientId !== clientId) {
        removePayPalScript();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } else if ((window as any).paypal) {
        scriptLoaded = true;
        loadedClientId = clientId;
        resolve();
        return;
      } else {
        existingScript.addEventListener('load', () => {
          scriptLoaded = true;
          loadedClientId = clientId;
          resolve();
        });
        existingScript.addEventListener('error', () => {
          const error = new Error('Le service de paiement PayPal est temporairement indisponible');
          scriptError = error;
          reject(error);
        });
        return;
      }
    }

    // Create and load the script
    const script = document.createElement('script');
    script.src = getPayPalScriptUrl(clientId);
    script.async = true;
    script.defer = true;
    script.setAttribute('data-sdk-integration-source', 'budgira');

    script.onload = () => {
      scriptLoaded = true;
      loadedClientId = clientId;
      scriptLoadingPromise = null;
      resolve();
    };

    script.onerror = () => {
      const error = new Error('Le service de paiement PayPal est temporairement indisponible');
      scriptError = error;
      scriptLoadingPromise = null;
      reject(error);
    };

    document.body.appendChild(script);
  });

  return scriptLoadingPromise;
}

/**
 * Custom hook for loading the PayPal JavaScript SDK dynamically.
 *
 * @param clientId - PayPal Client ID for SDK initialization (from server config)
 * @returns Object containing isLoaded, isLoading, and error states
 */
export function usePayPalScript(clientId: string): UsePayPalScriptReturn {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef<boolean>(true);
  const lastClientIdRef = useRef<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;

    // If clientId is empty, don't attempt to load - just wait
    // Don't set error here, let the parent component decide what to show
    if (!clientId || clientId.trim() === '') {
      setIsLoaded(false);
      setIsLoading(false);
      // Only set error if we had a clientId before and now it's empty
      if (lastClientIdRef.current && lastClientIdRef.current !== '') {
        setError(new Error('PayPal Client ID non configuré. Veuillez contacter l\'administrateur.'));
      }
      return;
    }

    // Check if clientId changed
    const clientIdChanged = lastClientIdRef.current !== null && lastClientIdRef.current !== clientId;
    lastClientIdRef.current = clientId;

    if (clientIdChanged) {
      console.log('[PayPal] Client ID changed, reloading script...');
      setIsLoaded(false);
      setError(null);
      removePayPalScript();
    }

    // If already loaded with same clientId, nothing to do
    if (scriptLoaded && loadedClientId === clientId) {
      setIsLoaded(true);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Start loading the script
    setIsLoading(true);
    setError(null);

    loadPayPalScript(clientId)
      .then(() => {
        if (mountedRef.current) {
          setIsLoaded(true);
          setIsLoading(false);
          setError(null);
        }
      })
      .catch((err: Error) => {
        if (mountedRef.current) {
          setIsLoaded(false);
          setIsLoading(false);
          setError(err);
        }
      });

    return () => {
      mountedRef.current = false;
    };
  }, [clientId]);

  return {
    isLoaded,
    isLoading,
    error,
  };
}

/**
 * Reset the PayPal script state (useful for testing)
 */
export function resetPayPalScriptState(): void {
  removePayPalScript();
}
